import { fetchBookingDates, fetchBookingsByDate } from '../../../api/adminApi';
import { i18nService } from '../../../i18n/I18nService';
import { buildMonthDays } from '../../calendarGrid';
import { dateKeyLocal as dateKey, isSameLocalDay as isSameDay } from '../../../utils/format';
import { t, createScreenHeader, createBackButton, isUnauthorized } from './context';
// "Today" as a UTC calendar day, materialised as a local-midnight Date whose
// local Y/M/D equals the UTC day — same trick bookingHistory.ts's calendar
// uses, so the "today" ring lines up with the UTC calendar-day convention the
// server filters bookings on, not the viewer's own timezone.
function startOfToday() {
    const now = new Date();
    return new Date(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
}
// Booking instants are Unix seconds on the UTC calendar-day convention the
// app uses everywhere — render with UTC getters so the interval matches the
// day the slot was booked on, with no per-viewer timezone shift.
function formatTimeRange(dateStart, dateEnd) {
    const start = new Date(dateStart * 1000);
    const end = new Date(dateEnd * 1000);
    const startLabel = `${String(start.getUTCHours()).padStart(2, '0')}:${String(start.getUTCMinutes()).padStart(2, '0')}`;
    const endLabel = `${String(end.getUTCHours()).padStart(2, '0')}:${String(end.getUTCMinutes()).padStart(2, '0')}`;
    return `${startLabel}–${endLabel}`;
}
export function showBookingsScreen(ctx) {
    ctx.screen.innerHTML = '';
    const header = createScreenHeader(t('admin.bookings.title'));
    const content = document.createElement('div');
    content.className = 'admin__content admin__form';
    const backButton = createBackButton(ctx, t('admin.bookings.back'));
    ctx.screen.append(header, content, backButton);
    renderBookingsCalendar(ctx, content);
}
function renderBookingsCalendar(ctx, content) {
    content.innerHTML = '';
    const today = startOfToday();
    let viewYear = today.getFullYear();
    let viewMonth = today.getMonth();
    let selectedDate = null;
    let markedDates = new Set();
    const calendar = document.createElement('div');
    calendar.className = 'admin__cal';
    const calHead = document.createElement('div');
    calHead.className = 'admin__cal-head';
    const prevButton = document.createElement('button');
    prevButton.type = 'button';
    prevButton.className = 'admin__cal-nav';
    prevButton.setAttribute('aria-label', t('admin.bookings.prevMonthLabel'));
    prevButton.innerHTML =
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M15 18l-6-6 6-6" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    const nextButton = document.createElement('button');
    nextButton.type = 'button';
    nextButton.className = 'admin__cal-nav';
    nextButton.setAttribute('aria-label', t('admin.bookings.nextMonthLabel'));
    nextButton.innerHTML =
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 6l6 6-6 6" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    const monthLabel = document.createElement('span');
    monthLabel.className = 'admin__cal-month';
    calHead.append(prevButton, monthLabel, nextButton);
    const weekdaysRow = document.createElement('div');
    weekdaysRow.className = 'admin__cal-weekdays';
    for (let day = 0; day < 7; day += 1) {
        const cell = document.createElement('span');
        cell.className = 'admin__cal-weekday';
        cell.textContent = t(`booking.calendar.weekdays.${day}`);
        weekdaysRow.appendChild(cell);
    }
    const calGrid = document.createElement('div');
    calGrid.className = 'admin__cal-grid';
    calendar.append(calHead, weekdaysRow, calGrid);
    const results = document.createElement('div');
    results.className = 'admin__booking-results';
    content.append(calendar, results);
    function renderCalendarGrid() {
        monthLabel.textContent = `${t(`booking.calendar.months.${viewMonth}`)} ${viewYear}`;
        calGrid.innerHTML = '';
        for (const date of buildMonthDays(viewYear, viewMonth)) {
            if (!date) {
                const empty = document.createElement('span');
                empty.className = 'admin__cal-day admin__cal-day--empty';
                calGrid.appendChild(empty);
                continue;
            }
            const key = dateKey(date);
            const button = document.createElement('button');
            button.type = 'button';
            button.className = 'admin__cal-day';
            button.textContent = String(date.getDate());
            if (isSameDay(date, today))
                button.classList.add('admin__cal-day--today-outline');
            if (date < today)
                button.classList.add('booking__cal-day--muted');
            if (markedDates.has(key))
                button.classList.add('admin__cal-day--has-bookings');
            if (selectedDate && isSameDay(date, selectedDate))
                button.classList.add('admin__cal-day--active');
            button.addEventListener('click', () => {
                selectedDate = date;
                renderCalendarGrid();
                loadBookingsForDate(ctx, results, key);
            });
            calGrid.appendChild(button);
        }
    }
    function loadMarkedDates() {
        const from = dateKey(new Date(viewYear, viewMonth, 1));
        const to = dateKey(new Date(viewYear, viewMonth + 1, 0));
        fetchBookingDates(from, to)
            .then((dates) => {
            markedDates = new Set(dates);
            renderCalendarGrid();
        })
            .catch((err) => {
            if (isUnauthorized(err)) {
                ctx.showLogin();
                return;
            }
            console.error('Failed to load booking dates:', err);
        });
    }
    prevButton.addEventListener('click', () => {
        viewMonth -= 1;
        if (viewMonth < 0) {
            viewMonth = 11;
            viewYear -= 1;
        }
        renderCalendarGrid();
        loadMarkedDates();
    });
    nextButton.addEventListener('click', () => {
        viewMonth += 1;
        if (viewMonth > 11) {
            viewMonth = 0;
            viewYear += 1;
        }
        renderCalendarGrid();
        loadMarkedDates();
    });
    renderCalendarGrid();
    loadMarkedDates();
}
function loadBookingsForDate(ctx, results, dateStr) {
    results.innerHTML = '';
    results.textContent = t('admin.bookings.loading');
    fetchBookingsByDate(dateStr, i18nService.getLocale())
        .then((bookings) => renderBookingsTable(ctx, results, bookings))
        .catch((err) => {
        if (isUnauthorized(err)) {
            ctx.showLogin();
            return;
        }
        console.error('Failed to load bookings for date:', err);
        results.innerHTML = '';
        results.textContent = t('admin.bookings.loadError');
    });
}
function renderBookingsTable(ctx, container, bookings) {
    container.innerHTML = '';
    if (bookings.length === 0) {
        const empty = document.createElement('p');
        empty.className = 'admin__table-empty';
        empty.textContent = t('admin.bookings.empty');
        container.appendChild(empty);
        return;
    }
    const table = document.createElement('table');
    table.className = 'admin__table';
    const thead = document.createElement('thead');
    const headRow = document.createElement('tr');
    const headers = [t('admin.bookings.table.customer'), t('admin.bookings.table.place'), t('admin.bookings.table.time')];
    for (const label of headers) {
        const th = document.createElement('th');
        th.textContent = label;
        headRow.appendChild(th);
    }
    thead.appendChild(headRow);
    table.appendChild(thead);
    const tbody = document.createElement('tbody');
    for (const booking of bookings) {
        const row = document.createElement('tr');
        const nameCell = document.createElement('td');
        const nameButton = document.createElement('button');
        nameButton.type = 'button';
        nameButton.className = 'admin__table-edit';
        nameButton.textContent = booking.customerName;
        nameButton.addEventListener('click', () => openContactModal(ctx, booking));
        nameCell.appendChild(nameButton);
        const placeCell = document.createElement('td');
        placeCell.textContent = booking.placeName;
        const timeCell = document.createElement('td');
        timeCell.textContent = formatTimeRange(booking.dateStart, booking.dateEnd);
        row.append(nameCell, placeCell, timeCell);
        tbody.appendChild(row);
    }
    table.appendChild(tbody);
    container.appendChild(table);
}
// Read-only contact-info popup: no confirm/cancel choice, just an "OK" that
// closes it — unlike every other admin__modal, which either edits something
// or asks a yes/no question.
function openContactModal(ctx, booking) {
    const overlay = document.createElement('div');
    overlay.className = 'admin__modal-overlay';
    overlay.addEventListener('click', (event) => {
        if (event.target === overlay)
            overlay.remove();
    });
    const modal = document.createElement('div');
    modal.className = 'admin__modal admin__modal--confirm';
    const title = document.createElement('h2');
    title.className = 'admin__modal-title';
    title.textContent = booking.customerName;
    const info = document.createElement('div');
    info.className = 'admin__price-tiers';
    const rows = [
        [t('admin.bookings.contactModal.nameLabel'), booking.customerName],
        [t('admin.bookings.contactModal.phoneLabel'), booking.customerPhone ?? '—'],
        [t('admin.bookings.contactModal.emailLabel'), booking.customerEmail],
    ];
    for (const [label, value] of rows) {
        const row = document.createElement('div');
        row.className = 'admin__price-tier';
        const rowLabel = document.createElement('span');
        rowLabel.className = 'admin__price-tier-label';
        rowLabel.textContent = label;
        const rowValue = document.createElement('span');
        rowValue.textContent = value;
        row.append(rowLabel, rowValue);
        info.appendChild(row);
    }
    const okButton = document.createElement('button');
    okButton.type = 'button';
    okButton.className = 'cta admin__submit';
    okButton.textContent = t('admin.bookings.contactModal.ok');
    okButton.addEventListener('click', () => overlay.remove());
    modal.append(title, info, okButton);
    overlay.appendChild(modal);
    ctx.section.appendChild(overlay);
}
